import { LOCAL_SEED } from "./localSeed";
import { DEMO_EMPLOYEE } from "../lib/roles";
import type {
  ChargeCode,
  ChargeCodeAccessRequest,
  CompanyTimekeepingPolicy,
  EmploymentStatus,
  TimesheetEntryNote,
  TimesheetHeader,
  TimesheetLine,
} from "../lib/timekeeping";

export const DEMO_COMPANY_HFS = {
  id: "co-hfs",
  name: "Harbor Federal Services",
};

export const DEMO_COMPANY_RIVER = {
  id: "co-river",
  name: "River Labs Subsidiary",
};

const EMPLOYMENT_STATUS_STORAGE_KEY = "govsuite.demo.employment_status.v1";

const DEFAULT_EMPLOYMENT_STATUS: Record<string, EmploymentStatus> = Object.fromEntries(
  LOCAL_SEED.employees.map((e) => [
    String(e.id),
    (String(e.employment_status || "FT") as EmploymentStatus),
  ]),
);

/** Resolve HR employment status for an employee (localStorage override → seed). */
export function employmentStatusFor(employeeId: string): EmploymentStatus {
  try {
    const raw = localStorage.getItem(EMPLOYMENT_STATUS_STORAGE_KEY);
    if (raw) {
      const map = JSON.parse(raw) as Record<string, EmploymentStatus>;
      if (map[employeeId]) return map[employeeId];
    }
  } catch {
    /* ignore */
  }
  return DEFAULT_EMPLOYMENT_STATUS[employeeId] ?? "FT";
}

export function loadEmploymentStatusMap(): Record<string, EmploymentStatus> {
  const base = { ...DEFAULT_EMPLOYMENT_STATUS };
  try {
    const raw = localStorage.getItem(EMPLOYMENT_STATUS_STORAGE_KEY);
    if (raw) Object.assign(base, JSON.parse(raw) as Record<string, EmploymentStatus>);
  } catch {
    /* ignore */
  }
  return base;
}

export function saveEmploymentStatusMap(map: Record<string, EmploymentStatus>) {
  localStorage.setItem(EMPLOYMENT_STATUS_STORAGE_KEY, JSON.stringify(map));
}

/** @deprecated Use employmentStatusFor + requiredHoursForPeriod */
export const DEMO_EMPLOYEE_FTE = 1;

export const SEED_CHARGE_CODES: ChargeCode[] = [
  {
    code: "01.014.000.0001.ENG",
    title: "NOAA Coastal Sensors — Engineering",
    description: "Direct engineering on NOAA Coastal Sensors (VV.CCC.PER.CLIN.TASK).",
    labor_type: "ST",
    class: "direct",
    lifecycle: "open",
    manager_name: "Jordan Ortiz",
    manager_employee_id: "emp-ortiz",
    status: "open",
    company_id: DEMO_COMPANY_HFS.id,
    authorized_employee_ids: [DEMO_EMPLOYEE.id, "emp-ortiz"],
    pop_start: "2025-01-01",
    pop_end: "2027-12-31",
    award_value: 2_400_000,
    ceiling_value: 2_500_000,
    funded_value: 1_800_000,
  },
  {
    code: "01.015.000.0001.OPS",
    title: "USACE Hydrology — Operations",
    description: "Operations support on USACE Hydrology.",
    labor_type: "ST",
    class: "direct",
    lifecycle: "open",
    manager_name: "Jordan Ortiz",
    manager_employee_id: "emp-ortiz",
    status: "open",
    company_id: DEMO_COMPANY_HFS.id,
    authorized_employee_ids: ["emp-ortiz"],
    pop_start: "2025-01-01",
    pop_end: "2027-06-30",
    award_value: 900_000,
    ceiling_value: 950_000,
    funded_value: 600_000,
  },
  {
    code: "01.016.000.0001.ENG",
    title: "GSA Facilities Mod — Engineering",
    description: "GSA facilities modification engineering (not yet authorized for demo employee).",
    labor_type: "ST",
    class: "direct",
    lifecycle: "open",
    manager_name: "Jordan Ortiz",
    manager_employee_id: "emp-ortiz",
    status: "open",
    company_id: DEMO_COMPANY_HFS.id,
    authorized_employee_ids: [],
    pop_start: "2026-01-01",
    pop_end: "2026-12-31",
    award_value: 500_000,
    ceiling_value: 500_000,
    funded_value: 250_000,
  },
  {
    code: "IND-GA-0001",
    title: "General & Administrative",
    description: "Indirect G&A pool (Finance-owned).",
    labor_type: "ST",
    class: "ga",
    lifecycle: "open",
    manager_name: "Morgan Blake",
    manager_employee_id: "emp-blake",
    status: "open",
    company_id: DEMO_COMPANY_HFS.id,
    authorized_employee_ids: [DEMO_EMPLOYEE.id, "emp-blake", "emp-ortiz"],
  },
  {
    code: "IND-BP-0001",
    title: "Bid & Proposal",
    description: "Indirect Bid & Proposal (CGO-owned).",
    labor_type: "ST",
    class: "bp",
    lifecycle: "open",
    manager_name: "Alex Rivera",
    manager_employee_id: "emp-cgo",
    status: "open",
    company_id: DEMO_COMPANY_HFS.id,
    authorized_employee_ids: ["emp-blake", "emp-cgo"],
  },
  {
    code: "ST-UNPAID-OT",
    title: "Uncompensated overtime (TTA)",
    description: "Unpaid hours worked beyond paid time — Total Time Accounting.",
    labor_type: "ST",
    class: "other_indirect",
    lifecycle: "open",
    manager_name: "Morgan Blake",
    manager_employee_id: "emp-blake",
    status: "open",
    company_id: DEMO_COMPANY_HFS.id,
    authorized_employee_ids: [DEMO_EMPLOYEE.id, "emp-ortiz", "emp-blake"],
    is_unpaid_ot: true,
  },
  {
    code: "PT-ACCRUED",
    title: "Accrued PT",
    labor_type: "PT",
    class: "leave",
    lifecycle: "open",
    manager_name: "Riley Chen",
    manager_employee_id: "emp-vp-hr",
    status: "open",
    company_id: DEMO_COMPANY_HFS.id,
    authorized_employee_ids: [DEMO_EMPLOYEE.id, "emp-ortiz", "emp-blake"],
  },
  {
    code: "PT-BANK",
    title: "Bank PT (M&A carryover)",
    labor_type: "PT",
    class: "leave",
    lifecycle: "open",
    manager_name: "Riley Chen",
    manager_employee_id: "emp-vp-hr",
    status: "open",
    company_id: DEMO_COMPANY_HFS.id,
    authorized_employee_ids: [DEMO_EMPLOYEE.id],
  },
  {
    code: "PT-HOLIDAY",
    title: "Company Holiday",
    alias: "HT",
    labor_type: "PT",
    class: "leave",
    lifecycle: "open",
    manager_name: "Riley Chen",
    manager_employee_id: "emp-vp-hr",
    status: "open",
    company_id: DEMO_COMPANY_HFS.id,
    authorized_employee_ids: [DEMO_EMPLOYEE.id, "emp-ortiz", "emp-blake"],
  },
  {
    code: "PT-SICK",
    title: "Sick Leave (PT)",
    labor_type: "PT",
    class: "leave",
    lifecycle: "open",
    manager_name: "Riley Chen",
    manager_employee_id: "emp-vp-hr",
    status: "open",
    company_id: DEMO_COMPANY_HFS.id,
    authorized_employee_ids: [DEMO_EMPLOYEE.id, "emp-ortiz", "emp-blake"],
  },
  {
    code: "AT-TRAINING",
    title: "Mandatory training",
    labor_type: "AT",
    class: "leave",
    lifecycle: "open",
    manager_name: "Riley Chen",
    manager_employee_id: "emp-vp-hr",
    status: "open",
    company_id: DEMO_COMPANY_HFS.id,
    authorized_employee_ids: [DEMO_EMPLOYEE.id, "emp-ortiz", "emp-blake"],
  },
  {
    code: "LWOP-PERSONAL",
    title: "Leave Without Pay — Personal",
    labor_type: "LWOP",
    class: "leave",
    lifecycle: "open",
    manager_name: "Riley Chen",
    manager_employee_id: "emp-vp-hr",
    status: "open",
    company_id: DEMO_COMPANY_HFS.id,
    authorized_employee_ids: [DEMO_EMPLOYEE.id],
  },
  {
    code: "01.009.20.0001.ENG",
    title: "Legacy Coastal Pilot (closed)",
    labor_type: "ST",
    class: "direct",
    lifecycle: "closed",
    manager_name: "Jordan Ortiz",
    manager_employee_id: "emp-ortiz",
    status: "closed",
    company_id: DEMO_COMPANY_HFS.id,
    authorized_employee_ids: [DEMO_EMPLOYEE.id],
    pop_start: "2020-01-01",
    pop_end: "2024-12-31",
  },
];

export const SEED_POLICIES: CompanyTimekeepingPolicy[] = [
  {
    id: "pol-hfs-1",
    company_id: DEMO_COMPANY_HFS.id,
    company_name: DEMO_COMPANY_HFS.name,
    cadence: "biweekly",
    hour_basis: "80_per_biweekly",
    hour_increment: 0.25,
    effective_date: "2025-01-01",
    end_date: "2026-09-30",
    require_daily_entry: true,
    late_entry_window_days: 1,
    total_time_accounting_required: true,
    unpaid_ot_charge_code: "ST-UNPAID-OT",
    certification_text_version: "v1-tta-2026-08",
  },
  {
    id: "pol-hfs-2",
    company_id: DEMO_COMPANY_HFS.id,
    company_name: DEMO_COMPANY_HFS.name,
    cadence: "weekly",
    hour_basis: "40_per_week",
    hour_increment: 0.25,
    effective_date: "2026-10-01",
    end_date: null,
    require_daily_entry: true,
    late_entry_window_days: 1,
    total_time_accounting_required: true,
    unpaid_ot_charge_code: "ST-UNPAID-OT",
    certification_text_version: "v1-tta-2026-08",
  },
  {
    id: "pol-river-1",
    company_id: DEMO_COMPANY_RIVER.id,
    company_name: DEMO_COMPANY_RIVER.name,
    cadence: "weekly",
    hour_basis: "40_per_week",
    hour_increment: 0.25,
    effective_date: "2025-06-01",
    end_date: null,
    require_daily_entry: true,
    late_entry_window_days: 1,
    total_time_accounting_required: true,
    unpaid_ot_charge_code: "ST-UNPAID-OT",
    certification_text_version: "v1-tta-2026-08",
  },
];

/** Period start dates (biweekly Mon–Sun pairs used in seed). */
const P = {
  p1: { start: "2026-06-15", end: "2026-06-28", due: "2026-06-30" },
  p2: { start: "2026-06-29", end: "2026-07-12", due: "2026-07-14" },
  p3: { start: "2026-07-13", end: "2026-07-26", due: "2026-07-28" },
  p4: { start: "2026-07-27", end: "2026-08-09", due: "2026-08-11" },
};

function fillWeekdays(
  start: string,
  end: string,
  codeHours: { code: string; hoursPerDay: number; days?: string[] }[],
): { lines: Omit<TimesheetLine, "id" | "timesheet_id">[] } {
  const days: string[] = [];
  {
    let cur = start;
    while (cur <= end) {
      days.push(cur);
      const d = new Date(cur + "T12:00:00");
      d.setDate(d.getDate() + 1);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      cur = `${y}-${m}-${day}`;
    }
  }
  const weekdays = days.filter((iso) => {
    const dow = new Date(iso + "T12:00:00").getDay();
    return dow !== 0 && dow !== 6;
  });

  const byCode = new Map<string, Record<string, number>>();
  for (const spec of codeHours) {
    const hours: Record<string, number> = { ...(byCode.get(spec.code) ?? {}) };
    const useDays = spec.days ?? weekdays;
    for (const d of useDays) {
      if (d >= start && d <= end) hours[d] = spec.hoursPerDay;
    }
    byCode.set(spec.code, hours);
  }

  const laborOf = (code: string) =>
    SEED_CHARGE_CODES.find((c) => c.code === code)?.labor_type ?? "PT";

  return {
    lines: [...byCode.entries()].map(([charge_code, hours]) => ({
      charge_code,
      labor_type: laborOf(charge_code),
      hours,
    })),
  };
}

function buildSheet(
  id: string,
  status: TimesheetHeader["status"],
  period: { start: string; end: string; due: string },
  employee: { id: string; name: string },
  lineSpecs: { code: string; hoursPerDay: number; days?: string[] }[],
  extra?: Partial<TimesheetHeader>,
): { header: TimesheetHeader; lines: TimesheetLine[] } {
  const { lines: raw } = fillWeekdays(period.start, period.end, lineSpecs);
  const lines: TimesheetLine[] = raw.map((l, i) => ({
    ...l,
    id: `${id}-L${i + 1}`,
    timesheet_id: id,
  }));
  return {
    header: {
      id,
      employee_id: employee.id,
      employee: employee.name,
      company_id: DEMO_COMPANY_HFS.id,
      period_start: period.start,
      period_end: period.end,
      status,
      due_at: period.due,
      ...extra,
    },
    lines,
  };
}

const chen = { id: DEMO_EMPLOYEE.id, name: DEMO_EMPLOYEE.name };
const ortiz = { id: "emp-ortiz", name: "Jordan Ortiz" };
const blake = { id: "emp-blake", name: "Morgan Blake" };
const patel = { id: "emp-patel", name: "Sam Patel" };

const s1 = buildSheet("ts-chen-p1", "Approved", P.p1, chen, [
  { code: "01.014.000.0001.ENG", hoursPerDay: 8 },
]);
const s2 = buildSheet("ts-chen-p2", "Approved", P.p2, chen, [
  { code: "01.014.000.0001.ENG", hoursPerDay: 8 },
]);
const s3 = buildSheet(
  "ts-chen-p3",
  "Approved",
  P.p3,
  chen,
  [
    {
      code: "01.014.000.0001.ENG",
      hoursPerDay: 8,
      days: [
        "2026-07-13",
        "2026-07-14",
        "2026-07-15",
        "2026-07-16",
        "2026-07-17",
        "2026-07-20",
        "2026-07-21",
        "2026-07-22",
      ],
    },
    { code: "01.014.000.0001.ENG", hoursPerDay: 4, days: ["2026-07-23"] },
    { code: "IND-GA-0001", hoursPerDay: 4, days: ["2026-07-23"] },
    { code: "PT-ACCRUED", hoursPerDay: 8, days: ["2026-07-24"] },
  ],
  { submitted_at: "2026-07-27T14:00:00.000Z", certification_at: "2026-07-27T14:00:00.000Z" },
);

/** Current open period — lines carried from p3 where total > 0, hours zeroed. */
const s4Lines: TimesheetLine[] = s3.lines
  .filter((l) => Object.values(l.hours).reduce((a, b) => a + b, 0) > 0)
  .map((l, i) => ({
    id: `ts-chen-p4-L${i + 1}`,
    timesheet_id: "ts-chen-p4",
    charge_code: l.charge_code,
    labor_type: l.labor_type,
    hours: {},
  }));

const s4: { header: TimesheetHeader; lines: TimesheetLine[] } = {
  header: {
    id: "ts-chen-p4",
    employee_id: chen.id,
    employee: chen.name,
    company_id: DEMO_COMPANY_HFS.id,
    period_start: P.p4.start,
    period_end: P.p4.end,
    status: "Draft",
    due_at: P.p4.due,
  },
  lines: s4Lines,
};

const o1 = buildSheet(
  "ts-ortiz-p4",
  "Submitted",
  P.p4,
  ortiz,
  [{ code: "01.015.000.0001.OPS", hoursPerDay: 8 }],
  { submitted_at: "2026-07-27T16:00:00.000Z", certification_at: "2026-07-27T16:00:00.000Z" },
);

const oLate1 = buildSheet("ts-ortiz-p1", "Approved", P.p1, ortiz, [
  { code: "01.015.000.0001.OPS", hoursPerDay: 8 },
]);
const oLate2 = buildSheet(
  "ts-ortiz-p2",
  "Draft",
  P.p2,
  ortiz,
  [{ code: "01.015.000.0001.OPS", hoursPerDay: 8 }],
); // past due draft = late
const oLate3 = buildSheet(
  "ts-ortiz-p3",
  "Draft",
  P.p3,
  ortiz,
  [{ code: "01.015.000.0001.OPS", hoursPerDay: 8 }],
);

const b1 = buildSheet("ts-blake-p3", "Rejected", P.p3, blake, [
  { code: "IND-GA-0001", hoursPerDay: 8 },
], { reject_reason: "Hours exceed period floor without OT approval." });

const bLate1 = buildSheet("ts-blake-p1", "Draft", P.p1, blake, [
  { code: "IND-GA-0001", hoursPerDay: 8 },
]);
const bLate2 = buildSheet("ts-blake-p2", "Draft", P.p2, blake, [
  { code: "IND-GA-0001", hoursPerDay: 8 },
]);
/** PT32 Blake: 80 hrs on an 80-basis period → over floor (min 64). */
const bSub = buildSheet(
  "ts-blake-p4",
  "Submitted",
  P.p4,
  blake,
  [{ code: "IND-GA-0001", hoursPerDay: 8 }],
  { submitted_at: "2026-07-28T10:00:00.000Z", certification_at: "2026-07-28T10:00:00.000Z" },
);

/** PTV Patel: variable hours, no minimum — submitted with 24 hrs. */
const pSub = buildSheet(
  "ts-patel-p4",
  "Submitted",
  P.p4,
  patel,
  [
    {
      code: "IND-GA-0001",
      hoursPerDay: 6,
      days: ["2026-07-27", "2026-07-28", "2026-07-29", "2026-07-30"],
    },
  ],
  { submitted_at: "2026-07-30T15:00:00.000Z", certification_at: "2026-07-30T15:00:00.000Z" },
);

export const SEED_TIMESHEETS: TimesheetHeader[] = [
  s4.header,
  s3.header,
  s2.header,
  s1.header,
  o1.header,
  oLate1.header,
  oLate2.header,
  oLate3.header,
  b1.header,
  bLate1.header,
  bLate2.header,
  bSub.header,
  pSub.header,
];

export const SEED_TIMESHEET_LINES: TimesheetLine[] = [
  ...s4.lines,
  ...s3.lines,
  ...s2.lines,
  ...s1.lines,
  ...o1.lines,
  ...oLate1.lines,
  ...oLate2.lines,
  ...oLate3.lines,
  ...b1.lines,
  ...bLate1.lines,
  ...bLate2.lines,
  ...bSub.lines,
  ...pSub.lines,
];

export const SEED_ENTRY_NOTES: TimesheetEntryNote[] = [
  {
    id: "note-1",
    timesheet_id: "ts-chen-p3",
    line_id: "ts-chen-p3-L1",
    date: "2026-07-23",
    old_hours: 8,
    new_hours: 4,
    note: "Moved afternoon to G&A for proposal support.",
    at: "2026-07-23T18:10:00.000Z",
    by: DEMO_EMPLOYEE.name,
  },
];

export const SEED_ACCESS_REQUESTS: ChargeCodeAccessRequest[] = [];

export const SEED_LEAVE_BEGIN = {
  employee_id: DEMO_EMPLOYEE.id,
  accrued_pt: 96,
  bank_pt: 24,
  unit: "hours" as const,
};

export const SEED_BURDEN_ASSIGNMENTS = [
  {
    id: "bur-1",
    employee_id: DEMO_EMPLOYEE.id,
    structure_name: "HFS Standard Fringe + OH",
    effective_date: "2025-01-01",
    end_date: null as string | null,
  },
  {
    id: "bur-2",
    employee_id: "emp-ortiz",
    structure_name: "HFS PM Fringe + OH",
    effective_date: "2025-01-01",
    end_date: null as string | null,
  },
];
